import React from 'react';
import { Container, Row, Col, Alert } from 'react-bootstrap';
import { WorkflowProvider, useWorkflow, PropertyInput } from './WorkflowProvider';
import StepIndicator from './StepIndicator';
import Step1PropertyInputStreamlined from './steps/Step1PropertyInput_Streamlined';
import Step2ProfessionalCMA from './steps/Step2ProfessionalCMA';
import Step3AIAnalysis from './steps/Step3AIAnalysis';
import Step4Dashboard from './steps/Step4Dashboard';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import WorkflowProgressTracker from './components/WorkflowProgressTracker';

const WorkflowContent: React.FC = () => {
  const { state, goToNextStep, goToPreviousStep, setError } = useWorkflow();
  const { currentStep, error } = state;
  
  // Store comparable analysis data for passing between steps and tracking
  const [comparableAnalysis, setComparableAnalysis] = React.useState<any>(null);

  const handleWorkflowRecovery = (recoveredStep: number) => {
    console.log('🔄 [WORKFLOW RECOVERY] Recovered to step:', recoveredStep);
  };

  const handleStep1Complete = async (data: PropertyInput) => {
    try {
      // Step 1 completed successfully
      
      // Enhanced validation before proceeding
      if (!data.property_address || data.property_address.trim() === '') {
        setError('Property address is required to proceed');
        return;
      }

      // Clear any previous errors
      setError(undefined);
      
      // Advance to next step
      goToNextStep();
    } catch (error) {
      console.error('❌ [WORKFLOW ERROR] Step 1 completion failed:', error);
      setError('Failed to advance to next step. Please try again.');
    }
  };

  const handleStep2Complete = async (analysis: any) => {
    try {
      // Step 2 completed successfully
      
      // Enhanced validation of analysis data
      if (!analysis || !analysis.success) {
        setError('CMA analysis failed. Please retry the analysis.');
        return;
      }

      // Check for comparables in different possible data structures
      const comparables = analysis.comparables || 
                         analysis.comparable_properties?.comparables || 
                         [];
      
      if (!comparables || comparables.length === 0) {
        setError('No comparable properties found. Please try a different property or radius.');
        return;
      }

      // Store analysis data for progress tracking
      setComparableAnalysis(analysis);
      setError(undefined); // Clear any errors
      
      // Advancing to Step 3
      goToNextStep();
    } catch (error) {
      console.error('❌ [WORKFLOW ERROR] Step 2 completion failed:', error);
      setError('Failed to advance to AI analysis. Please try again.');
    }
  };

  const handleStep2Back = () => {
    // Going back to Step 1
    goToPreviousStep();
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1PropertyInputStreamlined onComplete={handleStep1Complete} />;
      case 2:
        return <Step2ProfessionalCMA onComplete={handleStep2Complete} onBack={handleStep2Back} />;
      case 3:
        return <Step3AIAnalysis />;
      case 4:
        return <Step4Dashboard />;
      default:
        return <Step1PropertyInputStreamlined onComplete={handleStep1Complete} />;
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          {/* Global Error Display */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(undefined)}>
              <Alert.Heading>Workflow Error</Alert.Heading>
              <p>{error}</p>
            </Alert>
          )}

          {/* Progress Tracker with Recovery Options */}
          <WorkflowProgressTracker 
            onRecovery={handleWorkflowRecovery}
            showRecoveryOptions={true}
          />

          {/* Step Indicator */}
          <StepIndicator />
          
          {/* Current Step Content */}
          <div className="step-content">
            {renderCurrentStep()}
          </div>

          {/* Debug Information */}
          {process.env.NODE_ENV === 'development' && comparableAnalysis && (
            <details className="mt-4">
              <summary className="text-muted">Debug: Comparable Analysis Data</summary>
              <pre className="small bg-light p-2 rounded mt-2">
                {JSON.stringify(comparableAnalysis, null, 2)}
              </pre>
            </details>
          )}
        </Col>
      </Row>
    </Container>
  );
};

const SmartCMAWorkflow: React.FC = () => {
  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'CRM', url: '/' },
    { label: 'Smart CMA Workflow', url: '', active: true }
  ];

  return (
    <>
      <PageBreadcrumb items={breadcrumbItems} />
      <WorkflowProvider>
        <WorkflowContent />
      </WorkflowProvider>
    </>
  );
};

export default SmartCMAWorkflow; 